import { useMemo } from 'react';
import { Chip, Tooltip } from '@ds';
import { CoverageEntry } from '@/api/apis/facilities';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

interface RegionCoverageCellProps {
  coverage: CoverageEntry[] | null;
  serviceRegionId: string;
}

const MAX_VISIBLE = 5;

interface CountryChipProps {
  name: string;
  badge: string | number;
  subdivisions?: string[];
}

const CountryChip = ({ name, badge, subdivisions }: CountryChipProps) => {
  const chip = (
    <Chip size="sm">
      <span className="text-m-r">
        {name}
        <span className="ml-1 inline-flex items-center justify-center bg-white rounded-full min-w-4 h-4 px-1 text-xs font-medium leading-none">
          {badge}
        </span>
      </span>
    </Chip>
  );

  if (!subdivisions) return chip;

  return (
    <Tooltip placement="top" showArrow className="max-w-64" content={subdivisions.join(', ')}>
      {chip}
    </Tooltip>
  );
};

export const RegionCoverageCell = ({ coverage, serviceRegionId }: RegionCoverageCellProps) => {
  const { countries, resolveSubdivisionName } = useCountriesData();

  const allChipData = useMemo(
    () =>
      countries
        .filter((c) => c.serviceRegionId === serviceRegionId)
        .flatMap((country) => {
          const subdivisions = coverage?.find((e) => e.country === country.alpha2)?.subdivisions;
          if (!subdivisions || subdivisions.length === 0) return [];
          const isAll = subdivisions.length === 1 && subdivisions[0] === 'All';
          return [
            {
              country,
              subdivisions: isAll ? undefined : subdivisions.map((s) => resolveSubdivisionName(s, country.alpha2) ?? s),
              badge: isAll ? 'All' : subdivisions.length,
            },
          ];
        }),
    [countries, serviceRegionId, coverage, resolveSubdivisionName],
  );

  if (allChipData.length === 0) return <span className="text-m-m text-base-5">-</span>;

  const visibleData = allChipData.slice(0, MAX_VISIBLE);
  const hiddenData = allChipData.slice(MAX_VISIBLE);

  return (
    <div className="flex flex-wrap gap-1">
      {visibleData.map(({ country, subdivisions, badge }) => (
        <CountryChip key={country.alpha2} name={country.name} badge={badge} subdivisions={subdivisions} />
      ))}

      {hiddenData.length > 0 && (
        <Tooltip
          placement="top"
          showArrow
          className="max-w-64"
          content={hiddenData.map((d) => d.country.name).join(', ')}
        >
          <Chip size="sm">
            <span className="text-m-r">+{hiddenData.length}</span>
          </Chip>
        </Tooltip>
      )}
    </div>
  );
};
